import { products as mockProducts } from '../data/products'
import type { CartLine, Product } from '../types/commerce'

/* ────────────────────────────────────────────────────────────────
   SHOPIFY CONNECTION — fill these two values in to go live.

   1. In Shopify admin: Settings → Apps and sales channels →
      Develop apps → create an app → Storefront API access token.
   2. Paste the shop domain and the PUBLIC storefront token below.

   Nothing else in the app needs to change: every component reads
   products and creates checkouts through this file only.
   ──────────────────────────────────────────────────────────────── */
const SHOPIFY_DOMAIN = '' // e.g. 'beatsoda.myshopify.com'
const STOREFRONT_TOKEN = '' // public Storefront API access token
const API_VERSION = '2024-10'

export const isShopifyConnected = Boolean(SHOPIFY_DOMAIN && STOREFRONT_TOKEN)

/**
 * Shipping presentation config.
 * Rates themselves are never hard-coded — they are calculated by Shopify at
 * checkout from the shipping profiles configured in the store. Only the
 * promotional free-shipping threshold is surfaced in the UI.
 */
export const shipping = {
  freeShippingThreshold: { amount: 500, currencyCode: 'SEK' },
  note: 'Shipping and delivery options are calculated at checkout.',
}

export function formatMoney(amount: number, currencyCode = 'SEK'): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function pricePerCan(product: Product): string {
  return formatMoney(
    Math.round((product.price.amount / product.cans) * 10) / 10,
    product.price.currencyCode,
  )
}

async function storefront<T>(
  query: string,
  variables: Record<string, unknown> = {},
): Promise<T> {
  const response = await fetch(
    `https://${SHOPIFY_DOMAIN}/api/${API_VERSION}/graphql.json`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
    },
  )
  if (!response.ok) throw new Error('Shopify request failed')
  const json = await response.json()
  if (json.errors?.length) throw new Error(json.errors[0].message)
  return json.data as T
}

const PRODUCT_FIELDS = `
  id
  handle
  title
  description
  availableForSale
  images(first: 4) { edges { node { url altText } } }
  variants(first: 1) {
    edges {
      node {
        id
        title
        availableForSale
        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
      }
    }
  }
  packLabel: metafield(namespace: "beat", key: "pack_label") { value }
  cans: metafield(namespace: "beat", key: "cans") { value }
  tagline: metafield(namespace: "beat", key: "tagline") { value }
`

/* eslint-disable @typescript-eslint/no-explicit-any */
function mapProduct(node: any): Product {
  const variant = node.variants.edges[0]?.node
  const fallback = mockProducts[0]
  return {
    id: node.id,
    variantId: variant?.id ?? '',
    handle: node.handle,
    name: node.title,
    packLabel: node.packLabel?.value ?? variant?.title ?? '',
    cans: Number(node.cans?.value ?? 12),
    tagline: node.tagline?.value ?? '',
    description: node.description ?? '',
    price: {
      amount: Number(variant?.price?.amount ?? 0),
      currencyCode: variant?.price?.currencyCode ?? 'SEK',
    },
    compareAtPrice: variant?.compareAtPrice
      ? {
          amount: Number(variant.compareAtPrice.amount),
          currencyCode: variant.compareAtPrice.currencyCode,
        }
      : undefined,
    availableForSale: Boolean(node.availableForSale),
    images: node.images.edges.map((edge: any) => edge.node.url),
    // Ingredients, nutrition and details live as Shopify metafields once the
    // store is set up; until then they fall back to the brand copy.
    ingredients: fallback.ingredients,
    nutrition: fallback.nutrition,
    details: fallback.details,
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export async function getProducts(): Promise<Product[]> {
  if (!isShopifyConnected) return mockProducts
  const data = await storefront<{ products: { edges: { node: unknown }[] } }>(
    `query Products { products(first: 20, sortKey: MANUAL) { edges { node { ${PRODUCT_FIELDS} } } } }`,
  )
  return data.products.edges.map((edge) => mapProduct(edge.node))
}

export async function getProduct(handle: string): Promise<Product | null> {
  if (!isShopifyConnected) {
    return mockProducts.find((product) => product.handle === handle) ?? null
  }
  const data = await storefront<{ product: unknown }>(
    `query Product($handle: String!) { product(handle: $handle) { ${PRODUCT_FIELDS} } }`,
    { handle },
  )
  return data.product ? mapProduct(data.product) : null
}

/**
 * Creates a Shopify cart and returns the hosted checkout URL.
 * Payment, discount codes, shipping rates, taxes and the order confirmation
 * are all handled by Shopify — never by this application.
 */
export async function createCheckout(lines: CartLine[]): Promise<string | null> {
  if (!isShopifyConnected || lines.length === 0) return null
  const data = await storefront<{
    cartCreate: { cart: { checkoutUrl: string } | null }
  }>(
    `mutation CartCreate($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) {
        cart { checkoutUrl }
        userErrors { message }
      }
    }`,
    {
      lines: lines.map((line) => ({
        merchandiseId: line.product.variantId,
        quantity: line.quantity,
      })),
    },
  )
  return data.cartCreate.cart?.checkoutUrl ?? null
}
